import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "—");

const callbackMessage = () => {
	const params = new URLSearchParams(window.location.search);
	const result = params.get("quickbooks");
	if (result === "connected") return { severity: "success", text: "QuickBooks connected." };
	if (result === "error") {
		return {
			severity: "error",
			text: `QuickBooks connection failed: ${params.get("message") || "unknown error"}`,
		};
	}
	return null;
};

const QuickBooksTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	fetchQuickBooksStatus,
	connectQuickBooks,
	setupQuickBooksAccounts,
	syncQuickBooks,
	isSubmitting,
}) => {
	const [qb, setQb] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState(callbackMessage);

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	const load = async () => {
		if (!canLoad) {
			setQb(null);
			return;
		}
		setLoading(true);
		setError("");
		const result = await fetchQuickBooksStatus(organizationId);
		if (result.success) setQb(result.data);
		else setError(result.message);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [organizationId, requiresOrganizationSelection]);

	const handleConnect = async () => {
		const result = await connectQuickBooks(organizationId);
		if (result.success && result.url) window.location.assign(result.url);
	};

	const handleSetupAccounts = async () => {
		const result = await setupQuickBooksAccounts(organizationId);
		if (result.success) {
			const count = result.data?.accounts?.length ?? 0;
			setNotice({ severity: "success", text: `${count} account(s) linked or created in QuickBooks.` });
			load();
		}
	};

	const handleSync = async (retryFailed) => {
		const result = await syncQuickBooks(organizationId, retryFailed);
		if (result.success) {
			const data = result.data || {};
			setNotice(
				data.skipped
					? { severity: "info", text: data.skipped }
					: {
							severity: data.failed ? "warning" : "success",
							text: `Sent ${data.synced ?? 0} entr${data.synced === 1 ? "y" : "ies"}, ${data.failed ?? 0} failed.`,
					  }
			);
			load();
		}
	};

	const connection = qb?.connection;
	const counts = qb?.counts || {};

	return (
		<Box>
			<Box sx={{ mb: 2 }}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
			</Box>

			{notice && (
				<Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
					{notice.text}
				</Alert>
			)}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading && !qb ? (
				<CircularProgress size={24} />
			) : qb ? (
				<>
					<Card variant="outlined" sx={{ mb: 3 }}>
						<CardContent>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="h6">QuickBooks Online</Typography>
								<Chip
									size="small"
									color={qb.connected ? "success" : "default"}
									label={qb.connected ? "Connected" : "Not connected"}
								/>
								<Chip size="small" variant="outlined" label={qb.environment} />
								<Chip
									size="small"
									variant="outlined"
									color={qb.sync_enabled ? "success" : "warning"}
									label={qb.sync_enabled ? "Sync on" : "Sync off"}
								/>
							</Stack>

							{!qb.configured && (
								<Alert severity="warning" sx={{ mb: 2 }}>
									QuickBooks client ID / secret are not set on the server.
								</Alert>
							)}

							{connection ? (
								<Box sx={{ mb: 2 }}>
									<Typography variant="body2">
										Company: <strong>{connection.company_name || "—"}</strong> (realm{" "}
										{connection.realm_id})
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Connected {formatDateTime(connection.connected_at)} · token refreshed{" "}
										{formatDateTime(connection.last_refreshed_at)} · reconnect before{" "}
										{formatDateTime(connection.refresh_token_expires_at)}
									</Typography>
									{connection.last_error && (
										<Alert severity="error" sx={{ mt: 1 }}>
											{connection.last_error}
										</Alert>
									)}
								</Box>
							) : (
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Connect this organisation to a QuickBooks company to send posted journal
									entries there.
								</Typography>
							)}

							<Typography variant="body2" sx={{ mb: 2 }}>
								Synced <strong>{counts.synced ?? 0}</strong> · failed{" "}
								<strong>{counts.failed ?? 0}</strong> · waiting{" "}
								<strong>{counts.pending ?? 0}</strong> · accounts not yet in QuickBooks{" "}
								<strong>{qb.unmapped_accounts ?? 0}</strong>
							</Typography>

							<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
								<Button
									variant="contained"
									onClick={handleConnect}
									disabled={!qb.configured || isSubmitting}
								>
									{qb.connected ? "Reconnect QuickBooks" : "Connect QuickBooks"}
								</Button>
								<Button
									variant="outlined"
									onClick={handleSetupAccounts}
									disabled={!qb.connected || isSubmitting}
								>
									Set up accounts
								</Button>
								<Button
									variant="outlined"
									onClick={() => handleSync(false)}
									disabled={!qb.connected || isSubmitting}
								>
									Sync now
								</Button>
								<Button
									variant="outlined"
									color="warning"
									onClick={() => handleSync(true)}
									disabled={!qb.connected || isSubmitting || !counts.failed}
								>
									Retry failed
								</Button>
							</Stack>
						</CardContent>
					</Card>

					<Typography variant="h6" sx={{ mb: 1 }}>
						Failed syncs
					</Typography>
					{qb.failed?.length ? (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Entry</TableCell>
										<TableCell>Date</TableCell>
										<TableCell>Description</TableCell>
										<TableCell>Error</TableCell>
										<TableCell align="right">Attempts</TableCell>
										<TableCell>Last attempt</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{qb.failed.map((row) => (
										<TableRow key={row.journal_entry_id}>
											<TableCell>{row.entry_number}</TableCell>
											<TableCell>{row.entry_date}</TableCell>
											<TableCell>{row.description}</TableCell>
											<TableCell sx={{ color: "error.main", maxWidth: 360 }}>{row.error}</TableCell>
											<TableCell align="right">{row.attempts}</TableCell>
											<TableCell>{formatDateTime(row.last_attempt_at)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Typography variant="body2" color="text.secondary">
							No failed syncs.
						</Typography>
					)}
				</>
			) : null}
		</Box>
	);
};

export default QuickBooksTab;
